import {
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState, } from "react";
import endpoints from "utils/services/EndPoints";
import Api from "utils/services/Api";
import LoadingButton from "@mui/lab/LoadingButton";
import { getCodigoUsuario, } from "utils/Common";
import moment from 'moment';
import { Save } from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSnackbar } from "notistack";
import { validations } from "./validations";
import { makeStyles } from "@mui/styles";
import { DatePicker } from "@mui/x-date-pickers";
import ReadOnlyTextField from "components/input/ReadOnlyTextField";
import { dataAperturaCaja } from "utils/typesAperturaCaja";
import { FormInput } from "components/input/FormInput";
import Decimal from "decimal.js";

const useStyles = makeStyles(() => ({
  cardActions: {
    display: "flex",
    justifyContent: "space-between",
  },
}));

const estadosAperturaCaja = {
  A: "Abierta",
  C: "Cerrada",
}

const colorEstadosAperturaCaja = {
  A: "success",
  C: "error",
}

const factorMoneda = {
  B100: 100,
  B50: 50,
  B20: 20,
  B10: 10,
  B5: 5,
  B1: 1,
  M100: 1,
  M50: 0.50,
  M25: 0.25,
  M10: 0.10,
  M5: 0.05,
  M1: 0.01,
};

const monedaInitialValues = {
  B100: 0,
  B50: 0,
  B20: 0,
  B10: 0,
  B5: 0,
  B1: 0,
  M100: 0,
  M50: 0,
  M25: 0,
  M10: 0,
  M5: 0,
  M1: 0,
};

const commonInputProps = {
  type: "number",
  inputProps: {
    min: 0
  }
}

export default function AperturaCaja() {
  const { enqueueSnackbar } = useSnackbar();
  const [fechaCaja, setFechaCaja] = useState(moment())
  const [isSearchingApertura, setIsSearchingApertura] = useState(true)
  const [datos, setDatos] = useState(dataAperturaCaja);

  const classes = useStyles();

  const isOpening = datos.estado === ""
  const isClosing = datos.estado === "A"
  const canEdit = isOpening || isClosing

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
    isSubmitting,
    setValues,
  } = useFormik({
    initialValues: monedaInitialValues,
    validationSchema: yup.object().shape(validations),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const { detalles, ...restDatos } = datos
        const body = {
          ...restDatos,
          ...(isOpening ? { fecha: Date.now() } : {}),
          usuario: getCodigoUsuario(),
          estado: isOpening ? "A" : "C",
          detalles: detalles.map(d => ({
            ...d,
            [isOpening ? "valorApertura" : "valorCierre"]: values[d.denominacion],
          })),
        };

        const response = await Api(endpoints.aperturaCaja, "POST", body)
        console.log(response);
        enqueueSnackbar("Grabado correctamente", { variant: "success" })
      } catch (error) {
        enqueueSnackbar(error.message, { variant: "error" })
      } finally {
        setSubmitting(false)
      }
    },
  });

  const handleSearchApertura = useCallback(async (fechaConsulta) => {
    setIsSearchingApertura(true);
    try {
      const response = await Api(endpoints.consultarAperturaCaja, "POST", {
        codigo: "",
        usuario: getCodigoUsuario(),
        fecha: fechaConsulta,
        estado: "",
        valorApertura: "",
        ValorCierre: "",

      })
      setDatos(response)
      if (response.estado === "C") {
        const valoresCierreCaja = response.detalles.map(
          ({ denominacion, valorCierre }) => [
            denominacion,
            valorCierre
          ])
        setValues(Object.fromEntries(valoresCierreCaja))
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" })
      setDatos(dataAperturaCaja)
      setValues(monedaInitialValues)
    } finally {
      setIsSearchingApertura(false);
    }
  }, [enqueueSnackbar, setValues]);

  useEffect(() => {
    const result = Object.entries(values).reduce(
      (acc, [key, value]) => acc.plus(new Decimal(factorMoneda[key]).times(value || 0)),
      new Decimal(0)
    )
    setDatos(d => ({
      ...d,
      ...(isOpening ? {
        valorApertura: result.toNumber()
      } : {
        valorCierre: result.toNumber()
      }),
    }))
  }, [values, setDatos, isOpening])

  useEffect(() => {
    handleSearchApertura(fechaCaja)
  }, [fechaCaja, handleSearchApertura])

  return (
    <Grid container spacing={2}>
      <Grid item xs={8} md={6}>
        <Typography variant="h2">
          Caja
        </Typography>
      </Grid>
      <Grid item xs={12} component="form" onSubmit={handleSubmit}>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DatePicker
                  label="Fecha Caja"
                  id="fechaCaja"
                  value={fechaCaja}
                  onChange={(newValue) => {
                    setFechaCaja(newValue)
                  }}
                  renderInput={(params) => <TextField fullWidth {...params} />}
                  disableFuture
                  disabled={isSearchingApertura}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ReadOnlyTextField
                  disabled
                  id="valorApertura"
                  label="Valor Apertura"
                  value={datos.valorApertura}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ReadOnlyTextField
                  disabled
                  id="valorCierre"
                  label="Valor Cierre"
                  value={datos.valorCierre}
                />
              </Grid>
              <Grid container item xs={12} md={4} justifyContent="center" alignContent="center">
                <Chip
                  label={estadosAperturaCaja[datos.estado] ?? "Sin Aperturar"}
                  variant="filled"
                  color={colorEstadosAperturaCaja[datos.estado] ?? "warning"}
                />
              </Grid>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h3">
                    DETALLE BILLETES / MONEDAS
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="B100"
                    label="Cantidad Cien"
                    value={values.B100}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="M100"
                    label="Cantidad Monedas de Uno"
                    value={values.M100}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="B50"
                    label="Cantidad Cincuenta"
                    value={values.B50}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="M50"
                    label="Cantidad Monedas de Cincuenta"
                    value={values.M50}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="B20"
                    label="Cantidad Veinte"
                    value={values.B20}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="M25"
                    label="Cantidad Monedas de Veinte y Cinco"
                    value={values.M25}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="B10"
                    label="Cantidad Diez"
                    value={values.B10}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="M10"
                    label="Cantidad Monedas de Diez"
                    value={values.M10}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="B5"
                    label="Cantidad Cinco"
                    value={values.B5}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="M5"
                    label="Cantidad Monedas de Cinco"
                    value={values.M5}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="B1"
                    label="Cantidad Uno"
                    value={values.B1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    formName="M1"
                    label="Cantidad Monedas de Uno"
                    value={values.M1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isSubmitting || isSearchingApertura}
                    InputProps={{
                      readOnly: !canEdit,
                      ...commonInputProps,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions
            classes={{
              root: classes.cardActions
            }}
          >
            <LoadingButton
              type="submit"
              variant="outlined"
              color="primary"
              loading={isSubmitting}
              startIcon={
                <Save />
              }
              loadingPosition="start"
              disabled={!canEdit || isSubmitting || isSearchingApertura}
            >
              <span>
                {
                  isOpening ? "Aperturar Caja" : "Cerrar Caja"
                }
              </span>
            </LoadingButton>
          </CardActions>
        </Card>
      </Grid>
    </Grid>
  );
}
